import { useAuth } from "@/hooks/useAuth";
import { USER_TYPES } from "@/utils/roles";

// Component to conditionally render based on user role
export const RoleGuard = ({
  children,
  allowedRoles = [],
  requiredPermissions = [],
  requireAll = false,
  fallback = null,
  inverse = false,
  patientId = null,
  doctorId = null,
  practiceId = null,
}) => {
  const { role, hasAnyPermission, hasAllPermissions, canAccessPatient, canAccessDoctor, canAccessPractice } = useAuth();

  let hasAccess = true;

  // Early exit: If neither roles nor permissions specified, default to allow
  if (allowedRoles.length === 0 && requiredPermissions.length === 0) {
    hasAccess = true;
  } else {
    // Check in order of strictness - start with most restrictive
    
    // 1. Check permissions first (most granular/restrictive)
    if (requiredPermissions.length > 0) {
      if (requireAll) {
        hasAccess = hasAllPermissions(requiredPermissions);
      } else {
        hasAccess = hasAnyPermission(requiredPermissions);
      }
      
      // Early exit if permission check fails
      if (!hasAccess) {
        return inverse ? children : fallback;
      }
    }

    // 2. Check roles (less restrictive than permissions)
    if (allowedRoles.length > 0) {
      hasAccess = allowedRoles.includes(role);
      
      // Early exit if role check fails
      if (!hasAccess) {
        return inverse ? children : fallback;
      }
    }

    // 3. If both are specified and we reach here, both passed
    // hasAccess remains true from previous checks
  }

  // 4. Check relationship-based access (most restrictive)
  if (hasAccess && patientId) {
    hasAccess = canAccessPatient(patientId);
    if (!hasAccess) {
      return inverse ? children : fallback;
    }
  }

  if (hasAccess && doctorId) {
    hasAccess = canAccessDoctor(doctorId);
    if (!hasAccess) {
      return inverse ? children : fallback;
    }
  }

  if (hasAccess && practiceId) {
    hasAccess = canAccessPractice(practiceId);
    if (!hasAccess) {
      return inverse ? children : fallback;
    }
  }

  // Apply inverse logic if specified
  if (inverse) {
    hasAccess = !hasAccess;
  }

  return hasAccess ? children : fallback;
};

// Specific role-based components for common use cases
export const PatientOnly = ({ children, fallback = null }) => (
  <RoleGuard allowedRoles={[USER_TYPES.PATIENT]} fallback={fallback}>
    {children}
  </RoleGuard>
);

export const DoctorOnly = ({ children, fallback = null }) => (
  <RoleGuard allowedRoles={[USER_TYPES.DOCTOR]} fallback={fallback}>
    {children}
  </RoleGuard>
);

export const PracticeOwnerOnly = ({ children, fallback = null }) => (
  <RoleGuard allowedRoles={[USER_TYPES.PRACTICE_OWNER]} fallback={fallback}>
    {children}
  </RoleGuard>
);

export const DoctorOrPracticeOwner = ({ children, fallback = null }) => (
  <RoleGuard
    allowedRoles={[USER_TYPES.DOCTOR, USER_TYPES.PRACTICE_OWNER]}
    fallback={fallback}
  >
    {children}
  </RoleGuard>
);

export const StaffOnly = ({ children, fallback = null }) => (
  <RoleGuard
    allowedRoles={[USER_TYPES.DOCTOR, USER_TYPES.PRACTICE_OWNER]}
    fallback={fallback}
  >
    {children}
  </RoleGuard>
);

// Permission-based components
export const PermissionGuard = ({
  children,
  permissions = [],
  requireAll = false,
  fallback = null,
}) => (
  <RoleGuard
    requiredPermissions={permissions}
    requireAll={requireAll}
    fallback={fallback}
  >
    {children}
  </RoleGuard>
);

// Higher-order component for role-based rendering
export const withRoleGuard = (Component, guardProps = {}) => {
  return function GuardedComponent(props) {
    return (
      <RoleGuard {...guardProps}>
        <Component {...props} />
      </RoleGuard>
    );
  };
};

// Hook for conditional rendering in component logic
export const useRoleGuard = (
  allowedRoles = [],
  requiredPermissions = [],
  requireAll = false
) => {
  const { role, hasAnyPermission, hasAllPermissions } = useAuth();

  // Early exit: If neither roles nor permissions specified, default to allow
  if (allowedRoles.length === 0 && requiredPermissions.length === 0) {
    return true;
  }

  // Check in order of strictness - start with most restrictive
  
  // 1. Check permissions first (most granular/restrictive)
  if (requiredPermissions.length > 0) {
    const permissionAccess = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);
    
    // Early exit if permission check fails
    if (!permissionAccess) {
      return false;
    }
  }

  // 2. Check roles (less restrictive than permissions)
  if (allowedRoles.length > 0) {
    const roleAccess = allowedRoles.includes(role);
    
    // Early exit if role check fails
    if (!roleAccess) {
      return false;
    }
  }

  // If we reach here, all checks passed
  return true;
};
